import React, { useEffect, useState } from 'react';
import { Bell, X, Info, AlertTriangle, CheckCircle, MessageSquare } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
export type Notification = {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'message';
  read: boolean;
  timestamp: Date;
};
type NotificationSystemProps = {
  initialNotifications?: Notification[];
};
export const NotificationSystem: React.FC<NotificationSystemProps> = ({
  initialNotifications = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  // Calculate unread count
  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);
  // Demo: Add a notification after component mounts
  useEffect(() => {
    if (notifications.length === 0) {
      // Add some demo notifications if none exist
      const demoNotifications: Notification[] = [{
        id: '1',
        title: 'New Match Found',
        message: 'A new investment opportunity matches your criteria',
        type: 'info',
        read: false,
        timestamp: new Date(Date.now() - 1000 * 60 * 30) // 30 mins ago
      }, {
        id: '2',
        title: 'Project Update',
        message: 'Sustainable Rice Farming has reached 70% completion',
        type: 'success',
        read: false,
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2) // 2 hours ago
      }, {
        id: '3',
        title: 'Weather Alert',
        message: 'Heavy rain expected in Sacramento Valley region',
        type: 'warning',
        read: true,
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24) // 1 day ago
      }];
      setNotifications(demoNotifications);
    }
  }, [notifications.length]);
  const toggleNotifications = () => {
    setIsOpen(!isOpen);
  };
  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      read: true
    })));
  };
  const removeNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info size={18} className="text-blue-500" data-id="element-459" />;
      case 'warning':
        return <AlertTriangle size={18} className="text-yellow-500" data-id="element-460" />;
      case 'success':
        return <CheckCircle size={18} className="text-green-500" data-id="element-461" />;
      case 'message':
        return <MessageSquare size={18} className="text-purple-500" data-id="element-462" />;
      default:
        return <Info size={18} className="text-blue-500" data-id="element-463" />;
    }
  };
  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  return <div className="relative" data-id="element-464">
      {/* Notification Bell */}
      <button onClick={toggleNotifications} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Notifications" data-id="element-465">
        <Bell size={20} className="text-gray-700" data-id="element-466" />
        {unreadCount > 0 && <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 bg-leaf-green text-white text-xs font-bold rounded-full" data-id="element-467">
            {unreadCount}
          </span>}
      </button>
      {/* Notification Panel */}
      <AnimatePresence data-id="element-468">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg overflow-hidden z-50" data-id="element-469">
            <div className="border-b px-4 py-3 flex justify-between items-center" data-id="element-470">
              <h3 className="font-semibold" data-id="element-471">Notifications</h3>
              {unreadCount > 0 && <button onClick={markAllAsRead} className="text-sm text-leaf-green hover:underline" data-id="element-472">
                  Mark all as read
                </button>}
            </div>
            <div className="max-h-96 overflow-y-auto" data-id="element-473">
              {notifications.length === 0 ? <div className="py-6 text-center text-gray-500" data-id="element-474">
                  <p data-id="element-475">No notifications</p>
                </div> : notifications.map(notification => <motion.div key={notification.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${!notification.read ? 'bg-leaf-green/5' : ''}`} onClick={() => markAsRead(notification.id)} data-id="element-476">
                    <div className="flex items-start" data-id="element-477">
                      <div className="flex-shrink-0 mr-3" data-id="element-478">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-479">
                        <div className="flex justify-between items-start" data-id="element-480">
                          <p className={`text-sm font-medium ${!notification.read ? 'text-soil-brown' : 'text-gray-700'}`} data-id="element-481">
                            {notification.title}
                          </p>
                          <button onClick={e => {
                    e.stopPropagation();
                    removeNotification(notification.id);
                  }} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss" data-id="element-482">
                            <X size={14} data-id="element-483" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2" data-id="element-484">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-485">
                          {formatTime(notification.timestamp)}
                        </p>
                      </div>
                    </div>
                  </motion.div>)}
            </div>
            <div className="border-t px-4 py-2 text-center" data-id="element-486">
              <button className="text-sm text-leaf-green hover:underline" data-id="element-487">
                View all notifications
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};